import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, Crown, BookOpen, Clock, Users, CheckCircle, ExternalLink } from 'lucide-react';
import { trackEnrollmentClick, trackBundleClick } from '../components/GoogleAnalytics';
import { getAiCourseImage } from '../config/courseImages';

const BundleDetailPage = () => {
  const { bundleId } = useParams<{ bundleId: string }>();

  // Function to open all courses in bundle
  const handleEnrollInBundle = (courses: any[], bundleTitle?: string) => {
    // Track enrollment click
    trackEnrollmentClick(courses);
    trackBundleClick(bundleTitle || bundleId || '', bundleId || '');
    
    courses.forEach((course) => {
      window.open(`https://www.udemy.com/course/${course.udemy}/`, '_blank');
    });
  };


  const bundleData: Record<string, any> = {
    'qa-beginner': {
      title: 'QA Beginner Bundle',
      subtitle: 'Perfect Starting Point for Manual Testers',
      description: 'Complete foundation package for QA professionals new to AI testing. Start your AI journey with confidence.',
      badge: 'MOST RECOMMENDED',
      badgeColor: 'bg-green-500',
      bestSeller: true,
      level: 'Beginner',
      totalDuration: '28 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      students: '12,700+',
      rating: 4.8,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course - Start Here',
          description: 'Master the fundamentals of GenAI, prompt engineering, and basic automation integration for testing.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'AI Application Testing',
          description: 'Learn specialized frameworks for testing AI applications using DeepEval, RAGAs, and Ollama.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'GenAI Multi-Agent Systems Testing',
          subtitle: 'Advanced Multi-Agent Testing',
          description: 'Master testing complex multi-agent systems and enterprise-level AI workflows.',
          duration: '18 hours',
          lessons: '82 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '3,000+',
          whatYouLearn: [
            'Multi-agent system architecture',
            'Complex workflow testing',
            'Enterprise AI testing strategies',
            'Agent communication testing',
            'Scalability and performance optimization'
          ],
          udemy: 'genai-multi-agent-software-testing',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Sequential learning path from basics to advanced',
        'Complete foundation for AI testing career',
        'Hands-on projects and real-world scenarios',
        'Perfect for manual testers transitioning to AI',
        'Comprehensive coverage of essential AI testing tools'
      ],
      perfectFor: [
        'Manual QA Testers',
        'QA Engineers (0-3 years experience)',
        'Testing professionals new to AI',
        'Career switchers into QA',
        'Anyone wanting to future-proof their testing skills'
      ]
    },
    'qa-professional': {
      title: 'QA Professional Bundle',
      subtitle: 'Advanced AI Testing for Experienced QAs',
      description: 'Comprehensive AI testing mastery for experienced QA professionals ready to lead AI testing initiatives.',
      badge: 'POPULAR CHOICE',
      badgeColor: 'bg-blue-500',
      bestSeller: false,
      level: 'Intermediate',
      totalDuration: '53 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      students: '12,500+',
      rating: 4.9,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course',
          description: 'Master the fundamentals of GenAI, prompt engineering, and basic automation integration for testing.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Professional AI Testing',
          description: 'Advanced testing frameworks and methodologies for professional AI application testing.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'GenAI Multi-Agent Systems Testing',
          subtitle: 'Enterprise-Level Testing',
          description: 'Lead enterprise AI testing initiatives with advanced multi-agent system expertise.',
          duration: '18 hours',
          lessons: '82 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '3,000+',
          whatYouLearn: [
            'Enterprise multi-agent architectures',
            'Complex system integration testing',
            'AI testing team leadership',
            'Advanced performance optimization',
            'Enterprise deployment strategies'
          ],
          udemy: 'genai-multi-agent-software-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 4,
          title: 'AI-Driven Test Automation: Playwright, Selenium, LLMs & More',
          subtitle: 'Intelligent Self-Healing Test Frameworks',
          description: 'Build intelligent, resilient test automation frameworks with AI-powered self-healing locators for zero flaky tests.',
          duration: '15 hours',
          lessons: '70 lessons',
          level: 'Intermediate',
          rating: 5.0,
          students: '82+',
          whatYouLearn: [
            'Basics of Prompt Engineering, Context Engineering and Vibe Coding',
            'Applied Context Engineering and Prompt Engineering while writing Self healing code in Selenium/Playwright',
            'Writing Self Healing Selenium/Playwright code with ZERO flaky UI Tests',
            'Understand the Power of Local and Cloud LLMs while healing locators which are broken',
            'Install and configure local LLMs using Ollama',
            'Build code to access local LLMs via Ollama API',
            'Build code to access cloud LLMs via OpenAI API',
            'Implement AI Healing to get alternative locators from LLMs',
            'Build AI Healing for different locator types (XPath, CSS, ID, Name)',
            'Implement Self Healing for Playwright',
            'Understanding Vision Models and using them for Testing',
            'Implementing E2E Vision comparison'
          ],
          udemy: 'ai-test-automation-selenium-playwright-llms',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Advanced techniques for experienced professionals',
        'Leadership-focused content and strategies',
        'Enterprise-scale testing methodologies',
        'Perfect for QA leads and senior engineers',
        'Comprehensive coverage of professional AI testing'
      ],
      perfectFor: [
        'Senior QA Engineers (3+ years)',
        'QA Team Leads and Managers',
        'Testing Consultants',
        'QA Architects',
        'Professionals leading AI testing initiatives'
      ]
    },
    'sdet-focused': {
      title: 'SDET Complete Bundle',
      subtitle: 'Full-Stack AI Testing & Development',
      description: 'Complete AI testing and development package for SDETs who want to build and test AI systems end-to-end.',
      badge: 'COMPREHENSIVE',
      badgeColor: 'bg-purple-500',
      bestSeller: false,
      level: 'Advanced',
      totalDuration: '68 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      students: '13,700+',
      rating: 4.9,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course',
          description: 'Master the fundamentals of GenAI, prompt engineering, and automation integration for SDET workflows.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Advanced AI Application Testing',
          description: 'Professional-grade testing frameworks for comprehensive AI application validation and performance testing.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'Build Agentic AI & Multi-Agent System for Software Testing',
          subtitle: 'AI System Development & Testing',
          description: 'Complete guide to building, deploying, and testing AI agents, chatbots, and RAG systems from scratch.',
          duration: '15 hours',
          lessons: '78 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '1,200+',
          whatYouLearn: [
            'Fundamentals of LangChain & LangSmith',
            'Chat Message History in LangChain for storing conversation data',
            'Running Parallel & Multiple Chains (RunnableParallels, etc.)',
            'Building Chatbots with LangChain & Streamlit (with message history)',
            'Understanding Tools and Tool chains in LLM',
            'Building Tools and Custom Tools for LLM',
            'Creating AI Agents using LangChain',
            'Implementing RAG with vector stores & local LLM embeddings',
            'Using AI Agents and RAG with Tooling while building LLM Apps',
            'Optimizing & Debugging AI applications with LangSmith',
            'Evaluating & Testing LLM applications with RAGAs',
            'Real-world projects & hands-on testing strategies',
            'Assessing RAG & AI Agents with RAGAs'
          ],
          udemy: 'build-ai-agent-chatbot-rag-langchain-local-llm',
          image: '/api/placeholder/400/225'
        },
        {
          id: 4,
          title: 'GenAI Multi-Agent Systems Testing',
          subtitle: 'Enterprise Multi-Agent Systems',
          description: 'Master testing complex multi-agent systems and enterprise-level AI workflows for production environments.',
          duration: '18 hours',
          lessons: '82 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '3,000+',
          whatYouLearn: [
            'Complex multi-agent system architecture',
            'Enterprise workflow automation',
            'Advanced performance optimization',
            'Scalable AI testing strategies',
            'Production monitoring and debugging'
          ],
          udemy: 'genai-multi-agent-software-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 5,
          title: 'AI-Driven Test Automation: Playwright, Selenium, LLMs & More',
          subtitle: 'Intelligent Self-Healing Test Frameworks',
          description: 'Build intelligent, resilient test automation frameworks with AI-powered self-healing locators for zero flaky tests.',
          duration: '15 hours',
          lessons: '70 lessons',
          level: 'Intermediate',
          rating: 5.0,
          students: '82+',
          whatYouLearn: [
            'Basics of Prompt Engineering, Context Engineering and Vibe Coding',
            'Applied Context Engineering and Prompt Engineering while writing Self healing code in Selenium/Playwright',
            'Writing Self Healing Selenium/Playwright code with ZERO flaky UI Tests',
            'Understand the Power of Local and Cloud LLMs while healing locators which are broken',
            'Install and configure local LLMs using Ollama',
            'Build code to access local LLMs via Ollama API',
            'Build code to access cloud LLMs via OpenAI API',
            'Implement AI Healing to get alternative locators from LLMs',
            'Build AI Healing for different locator types (XPath, CSS, ID, Name)',
            'Implement Self Healing for Playwright',
            'Understanding Vision Models and using them for Testing',
            'Implementing E2E Vision comparison'
          ],
          udemy: 'ai-test-automation-selenium-playwright-llms',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Complete SDET skill transformation',
        'Build and test AI systems end-to-end',
        'Advanced automation and CI/CD integration',
        'Production-ready AI development skills',
        'Comprehensive coverage of AI testing tools'
      ],
      perfectFor: [
        'SDET Engineers',
        'Test Automation Engineers',
        'Full-Stack QA Engineers',
        'Technical QA Leads',
        'Developers transitioning to testing'
      ]
    },
    'sdet-dev-master': {
      title: 'SDET + Dev Master Bundle',
      subtitle: 'Complete AI Engineering Mastery',
      description: 'Ultimate package for technical professionals who want to master both AI development and testing, including model fine-tuning and custom AI implementations.',
      badge: 'EXPERT LEVEL',
      badgeColor: 'bg-orange-500',
      bestSeller: false,
      level: 'Expert',
      totalDuration: '73 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
      students: '14,900+',
      rating: 4.9,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Advanced Foundation',
          description: 'Master GenAI fundamentals with advanced techniques for enterprise-level AI development and testing.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Professional AI Testing',
          description: 'Enterprise-grade testing frameworks for comprehensive AI application validation and performance optimization.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'Build Agentic AI & Multi-Agent System for Software Testing',
          subtitle: 'AI System Architecture',
          description: 'Advanced AI system development including complex agents, intelligent chatbots, and sophisticated RAG implementations.',
          duration: '15 hours',
          lessons: '78 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '1,200+',
          whatYouLearn: [
            'Fundamentals of LangChain & LangSmith',
            'Chat Message History in LangChain for storing conversation data',
            'Running Parallel & Multiple Chains (RunnableParallels, etc.)',
            'Building Chatbots with LangChain & Streamlit (with message history)',
            'Understanding Tools and Tool chains in LLM',
            'Building Tools and Custom Tools for LLM',
            'Creating AI Agents using LangChain',
            'Implementing RAG with vector stores & local LLM embeddings',
            'Using AI Agents and RAG with Tooling while building LLM Apps',
            'Optimizing & Debugging AI applications with LangSmith',
            'Evaluating & Testing LLM applications with RAGAs',
            'Real-world projects & hands-on testing strategies',
            'Assessing RAG & AI Agents with RAGAs'
          ],
          udemy: 'build-ai-agent-chatbot-rag-langchain-local-llm',
          image: '/api/placeholder/400/225'
        },
        {
          id: 4,
          title: 'GenAI Multi-Agent Systems Testing',
          subtitle: 'Advanced System Integration',
          description: 'Master complex multi-agent systems, advanced orchestration, and enterprise-scale AI architecture patterns.',
          duration: '18 hours',
          lessons: '82 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '3,000+',
          whatYouLearn: [
            'Advanced multi-agent orchestration',
            'Enterprise AI system architecture',
            'Advanced performance optimization',
            'Complex system integration patterns',
            'AI system scaling strategies'
          ],
          udemy: 'genai-multi-agent-software-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 5,
          title: 'LLM Fine-Tuning for Beginners: HuggingFace & Unsloth',
          subtitle: 'Advanced AI Development',
          description: 'Master advanced AI techniques including model fine-tuning, custom model development, and specialized AI implementations.',
          duration: '20 hours',
          lessons: '95 lessons',
          level: 'Expert',
          rating: 4.9,
          students: '1,200+',
          whatYouLearn: [
            'Introduction to Machine Learning',
            'Working with Natural Language Processing (NLP) Libraries',
            'In-Depth Understanding of the Transformers Library',
            'Various Ways to Work with Large Language Models (LLMs)',
            'Functional Testing of AI Models',
            'Bias and Fairness Testing',
            'Evaluating AI Models',
            'Working with AI Agents',
            'Fine-tuning and Training AI Models'
          ],
          udemy: 'ai-with-huggingface',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Complete AI engineering mastery',
        'Advanced model development and fine-tuning',
        'Enterprise-scale AI system architecture',
        'Production-ready AI development skills',
        'Comprehensive AI/ML engineering expertise'
      ],
      perfectFor: [
        'Senior SDET Engineers',
        'AI/ML Engineers',
        'Technical Architects',
        'AI Development Leads',
        'Full-Stack AI Developers'
      ]
    },
    // Persona-specific bundles
    'new-to-ai': {
      title: 'New to AI Testing',
      subtitle: 'Perfect Foundation for QA Beginners',
      description: 'Start your AI testing journey with the essential foundation course. Learn core concepts before advancing.',
      badge: 'FOUNDATION',
      badgeColor: 'bg-blue-500',
      bestSeller: false,
      level: 'Beginner',
      totalDuration: '8 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      students: '8,000+',
      rating: 4.7,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course - Start Here',
          description: 'Master the fundamentals of GenAI, prompt engineering, and basic automation integration for testing.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Perfect starting point for AI testing',
        'No prior AI experience required',
        'Hands-on practical examples',
        'Foundation for advanced courses',
        'Comprehensive GenAI introduction'
      ],
      perfectFor: [
        'Manual QA Testers',
        'QA Engineers new to AI',
        'Testing professionals seeking AI basics',
        'Career switchers into AI testing',
        'Anyone starting their AI journey'
      ]
    },
    'automation-engineer': {
      title: 'Automation Engineer Path',
      subtitle: 'AI Integration for Test Automation Experts',
      description: 'Perfect combination for automation engineers ready to integrate AI capabilities into their testing workflows.',
      badge: 'AUTOMATION FOCUS',
      badgeColor: 'bg-green-500',
      bestSeller: false,
      level: 'Intermediate',
      totalDuration: '20 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      students: '9,500+',
      rating: 4.8,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course',
          description: 'Master the fundamentals of GenAI, prompt engineering, and basic automation integration for testing.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Framework Mastery Course',
          description: 'Learn specialized frameworks for testing AI applications using DeepEval, RAGAs, and Ollama.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Perfect for automation engineers',
        'Seamless AI integration techniques',
        'Framework-focused learning',
        'CI/CD integration strategies',
        'Practical automation examples'
      ],
      perfectFor: [
        'Test Automation Engineers',
        'QA Engineers with automation experience',
        'DevOps professionals',
        'Technical QA leads',
        'Automation framework developers'
      ]
    },
    'ai-enthusiast': {
      title: 'AI Enthusiast Path',
      subtitle: 'Comprehensive AI Development, Testing & Security',
      description: 'Complete path for AI enthusiasts who want to build, test, and secure AI systems from end-to-end.',
      badge: 'AI SPECIALIST',
      badgeColor: 'bg-purple-500',
      bestSeller: false,
      level: 'Advanced',
      totalDuration: '53 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      students: '10,700+',
      rating: 4.8,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Foundation Course',
          description: 'Master the fundamentals of GenAI, prompt engineering, and automation integration.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Framework Expertise',
          description: 'Advanced testing frameworks for comprehensive AI application validation.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Professional DeepEval implementation',
            'Advanced RAGAs strategies',
            'Enterprise Ollama deployments',
            'AI model performance optimization',
            'Professional testing workflows'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'Build Agentic AI & Multi-Agent System for Software Testing',
          subtitle: 'AI Development & Testing',
          description: 'Complete guide to building, deploying, and testing AI agents and chatbots.',
          duration: '15 hours',
          lessons: '78 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '1,200+',
          whatYouLearn: [
            'Fundamentals of LangChain & LangSmith',
            'Chat Message History in LangChain for storing conversation data',
            'Running Parallel & Multiple Chains (RunnableParallels, etc.)',
            'Building Chatbots with LangChain & Streamlit (with message history)',
            'Understanding Tools and Tool chains in LLM',
            'Building Tools and Custom Tools for LLM',
            'Creating AI Agents using LangChain',
            'Implementing RAG with vector stores & local LLM embeddings',
            'Using AI Agents and RAG with Tooling while building LLM Apps',
            'Optimizing & Debugging AI applications with LangSmith',
            'Evaluating & Testing LLM applications with RAGAs',
            'Real-world projects & hands-on testing strategies',
            'Assessing RAG & AI Agents with RAGAs'
          ],
          udemy: 'build-ai-agent-chatbot-rag-langchain-local-llm',
          image: '/api/placeholder/400/225'
        },
        {
          id: 4,
          title: 'Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents',
          subtitle: 'Practical AI Security Testing - NEW',
          description: 'Find and fix security weaknesses in RAG applications, chatbots, and AI agents using Promptfoo and real-world adversarial attacks.',
          duration: '8 hours',
          lessons: '66 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '47+',
          whatYouLearn: [
            'Configure Promptfoo targets, providers, plugins, policies, strategies, graders, and assertions',
            'Run jailbreak, prompt-injection, PII-leakage, hallucination, and excessive-agency attacks',
            'Test RAG applications for grounding, source attribution, indirect prompt injection, and knowledge-base poisoning',
            'Use local and hosted red-team providers, including Ollama-based local LLMs',
            'Analyze Promptfoo reports and build repeatable AI security regression tests'
          ],
          udemy: 'ai-red-teaming-promptfoo-rag-chatbots-agents',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Complete AI development and testing skills',
        'Build and test your own AI systems',
        'Practical AI red teaming and RAG security testing',
        'Advanced framework expertise',
        'Production-ready knowledge'
      ],
      perfectFor: [
        'AI Enthusiasts',
        'Full-Stack QA Engineers',
        'Developers transitioning to AI',
        'Technical leads exploring AI',
        'AI product developers'
      ]
    },
    'senior-engineer': {
      title: 'Senior Engineer Mastery',
      subtitle: 'Complete AI Engineering & Testing Expertise',
      description: 'Ultimate comprehensive path for senior engineers who want to master every aspect of AI development and testing.',
      badge: 'EXPERT MASTERY',
      badgeColor: 'bg-orange-500',
      bestSeller: false,
      level: 'Expert',
      totalDuration: '73 hours',
      originalPrice: 250,
      bundlePrice: 120,
      savings: 130,
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
      students: '14,900+',
      rating: 4.9,
      courses: [
        {
          id: 0,
          title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
          subtitle: 'No-Code AI Testing - NEW',
          description: 'Automate software tests without writing a single line of code. Describe tests in plain English and let AI handle the rest.',
          duration: '10 hours',
          lessons: '40 lessons',
          level: 'Beginner',
          rating: 5.0,
          students: '1,000+',
          whatYouLearn: [
            'Write automated tests in plain English',
            'AI-powered test generation and execution',
            'Test maintenance with AI assistance',
            'No-code automation workflows'
          ],
          udemy: 'zero-code-ai-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 1,
          title: 'Using Gen AI & AI Agent in Software Automation Testing',
          subtitle: 'Advanced Foundation',
          description: 'Master GenAI fundamentals with advanced techniques for enterprise-level development.',
          duration: '8 hours',
          lessons: '45 lessons',
          level: 'Beginner',
          rating: 4.7,
          students: '8,000+',
          whatYouLearn: [
            'Introduction to Generative AI',
            'Running Large Language Models (LLMs) Locally',
            'Manual Testing with Gen AI',
            'Automated UI Testing',
            'Playwright UI Testing',
            'No-code Automation with TestRigor',
            'API Testing',
            'Using Gen AI APIs to Add Intelligence with Self-healing locators to Test Automation',
            'Using Model Context Protocol'
          ],
          udemy: 'generative-ai-in-software-automation-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 2,
          title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
          subtitle: 'Enterprise Testing',
          description: 'Enterprise-grade testing frameworks for comprehensive AI application validation.',
          duration: '12 hours',
          lessons: '65 lessons',
          level: 'Intermediate',
          rating: 4.9,
          students: '1,500+',
          whatYouLearn: [
            'Foundations of AI Application Testing',
            'Local LLM Deployment with Ollama',
            'Environment Setup',
            'DeepEval Basics',
            'Advanced LLM Evaluation',
            'RAG Testing with DeepEval',
            'Advanced RAG Testing with DeepEval',
            'Testing AI Agents and Tool Callings',
            'Evaluating LLMs using RAGAS',
            'Testing RAG applications with RAGAS'
          ],
          udemy: 'ai-testing-deepeval-ragas-ollama',
          image: '/api/placeholder/400/225'
        },
        {
          id: 3,
          title: 'Build Agentic AI & Multi-Agent System for Software Testing',
          subtitle: 'Advanced AI Architecture',
          description: 'Advanced AI system development including complex agents and sophisticated implementations.',
          duration: '15 hours',
          lessons: '78 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '1,200+',
          whatYouLearn: [
            'Fundamentals of LangChain & LangSmith',
            'Chat Message History in LangChain for storing conversation data',
            'Running Parallel & Multiple Chains (RunnableParallels, etc.)',
            'Building Chatbots with LangChain & Streamlit (with message history)',
            'Understanding Tools and Tool chains in LLM',
            'Building Tools and Custom Tools for LLM',
            'Creating AI Agents using LangChain',
            'Implementing RAG with vector stores & local LLM embeddings',
            'Using AI Agents and RAG with Tooling while building LLM Apps',
            'Optimizing & Debugging AI applications with LangSmith',
            'Evaluating & Testing LLM applications with RAGAs',
            'Real-world projects & hands-on testing strategies',
            'Assessing RAG & AI Agents with RAGAs'
          ],
          udemy: 'build-ai-agent-chatbot-rag-langchain-local-llm',
          image: '/api/placeholder/400/225'
        },
        {
          id: 4,
          title: 'GenAI Multi-Agent Systems Testing',
          subtitle: 'Enterprise System Integration',
          description: 'Master complex multi-agent systems and enterprise-scale AI architecture patterns.',
          duration: '18 hours',
          lessons: '82 lessons',
          level: 'Advanced',
          rating: 4.8,
          students: '3,000+',
          whatYouLearn: [
            'Advanced multi-agent orchestration',
            'Enterprise AI system architecture',
            'Advanced performance optimization',
            'Complex system integration patterns',
            'AI system scaling strategies'
          ],
          udemy: 'genai-multi-agent-software-testing',
          image: '/api/placeholder/400/225'
        },
        {
          id: 5,
          title: 'LLM Fine-Tuning for Beginners: HuggingFace & Unsloth',
          subtitle: 'Advanced Model Development',
          description: 'Master advanced AI techniques including model fine-tuning and custom model development.',
          duration: '20 hours',
          lessons: '95 lessons',
          level: 'Expert',
          rating: 4.9,
          students: '1,200+',
          whatYouLearn: [
            'Introduction to Machine Learning',
            'Working with Natural Language Processing (NLP) Libraries',
            'In-Depth Understanding of the Transformers Library',
            'Various Ways to Work with Large Language Models (LLMs)',
            'Functional Testing of AI Models',
            'Bias and Fairness Testing',
            'Evaluating AI Models',
            'Working with AI Agents',
            'Fine-tuning and Training AI Models'
          ],
          udemy: 'ai-with-huggingface',
          image: '/api/placeholder/400/225'
        }
      ],
      bundleBenefits: [
        'Complete AI engineering mastery',
        'Advanced model development skills',
        'Enterprise-scale system architecture',
        'Production-ready expertise',
        'Leadership-level AI knowledge'
      ],
      perfectFor: [
        'Senior Engineers & Architects',
        'AI/ML Engineering Leaders',
        'Technical Directors',
        'AI Product Architects',
        'Enterprise AI Consultants'
      ]
    }
  };

  const bundle = bundleData[bundleId || ''];

  if (!bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Bundle Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`${bundle.bgColor} border-b ${bundle.borderColor}`}>
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          {/* Back Button */}
          <Link 
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Bundles
          </Link>

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1 w-full lg:w-auto">
              {/* Badge */}
              <div className="mb-4">
                <div className={`inline-flex items-center ${bundle.badgeColor} text-white px-3 py-2 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold shadow-lg space-x-2`}>
                  {bundle.bestSeller && <Crown className="w-3 h-3 md:w-4 md:h-4" />}
                  <span>{bundle.badge}</span>
                  {bundle.bestSeller && <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />}
                </div>
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">{bundle.title}</h1>
              <p className="text-lg md:text-xl font-semibold text-gray-700 mb-4">{bundle.subtitle}</p>
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">{bundle.description}</p>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {bundle.students} students
                </div>
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-2 text-yellow-500 fill-current" />
                  {bundle.rating} rating
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {bundle.totalDuration} total
                </div>
                <div className="flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {bundle.courses.length} courses
                </div>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 w-full lg:min-w-[300px] lg:max-w-[300px]">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <span className="text-3xl font-bold text-gray-900">${bundle.bundlePrice}</span>
                  <span className="text-lg text-gray-500 line-through">${bundle.originalPrice}</span>
                </div>
                <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                  Save ${bundle.savings}
                </span>
              </div>
              <button 
                onClick={() => handleEnrollInBundle(bundle.courses, bundle.title)}
                className={`w-full bg-gradient-to-r ${bundle.color} text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 mb-4`}
              >
                Enroll in Complete Bundle
              </button>
              <p className="text-xs text-gray-500 text-center">30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Courses Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Courses Included</h2>
              <div className="space-y-6">
                {bundle.courses.map((course: any, index: number) => (
                  <div 
                    key={course.id}
                    className="bg-white rounded-2xl p-4 md:p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
                    onClick={() => window.open(`https://www.udemy.com/course/${course.udemy}/`, '_blank')}
                  >
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[auto_10rem_minmax(0,1fr)] items-start gap-4 md:gap-6">
                      {/* Course Number */}
                      <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${bundle.color} rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-base md:text-lg`}>
                        {index + 1}
                      </div>

                      <img
                        src={getAiCourseImage(course.udemy) ?? course.image}
                        alt={`${course.title} course banner`}
                        loading="lazy"
                        className="w-full aspect-video object-cover rounded-xl border border-gray-200 shadow-sm"
                      />

                      {/* Course Info */}
                      <div className="col-span-2 md:col-span-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                          <div>
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {course.title}
                            </h3>
                            <p className="text-green-600 font-semibold text-sm md:text-base">{course.subtitle}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 md:w-5 md:h-5 text-gray-400 group-hover:text-blue-600 transition-colors self-start sm:self-center" />
                        </div>
                        
                        <p className="text-gray-600 mb-4 leading-relaxed">{course.description}</p>

                        {/* Course Stats */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {course.duration}
                          </span>
                          <span className="flex items-center">
                            <BookOpen className="w-4 h-4 mr-1" />
                            {course.lessons}
                          </span>
                          <span className="flex items-center">
                            <Star className="w-4 h-4 mr-1 text-yellow-500 fill-current" />
                            {course.rating}
                          </span>
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {course.students}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            course.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                            course.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {course.level}
                          </span>
                        </div>

                        {/* What You'll Learn */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">What You'll Learn:</h4>
                          <div className="grid grid-cols-1 gap-2">
                            {course.whatYouLearn.map((item: string, itemIndex: number) => (
                              <div key={itemIndex} className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 text-sm">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Bundle Benefits */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bundle Benefits</h3>
                <div className="space-y-3">
                  {bundle.bundleBenefits.map((benefit: string, index: number) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Perfect For */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Perfect For</h3>
                <div className="space-y-2">
                  {bundle.perfectFor.map((role: string, index: number) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-700">{role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Card */}
              <div className={`${bundle.bgColor} rounded-2xl p-6 border ${bundle.borderColor} text-center`}>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Ready to Start?</h3>
                <button 
                  onClick={() => handleEnrollInBundle(bundle.courses, bundle.title)}
                  className={`w-full bg-gradient-to-r ${bundle.color} text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 mb-3`}
                >
                  <Crown className="w-5 h-5 mr-2 inline" />
                  Get {bundle.title}
                </button>
                <p className="text-sm text-gray-600">Start your AI testing journey today</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleDetailPage;
